import { Crown, Zap, Star, Info } from "lucide-react";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { usePlansQuery } from "@/generated/graphql";
import { useMemo, useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { createWhatsAppUrl } from "@/constants/contact";
import { useNavigate } from "react-router-dom";
import LoginRegisterModal from "@/components/LoginRegisterModal";
import { captureUtmParamsFromUrl } from "@/lib/utm-params";
import { useCurrency, getPlanPrices } from "@/hooks/use-currency";
import { computeMaxYearlySavingsPct } from "@/lib/billing/computeMaxYearlySavingsPct";
import { WalletPlanDiscountNote } from "@/components/billing/WalletPlanDiscountNote";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const PlansList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatCurrency, isBRL } = useCurrency();
  const { data, loading, error } = usePlansQuery();
  const apiPlans = (data?.plans ?? []).filter(Boolean).sort((a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0));

  const [isYearly, setIsYearly] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  // Capture UTM params from URL on mount
  useEffect(() => {
    captureUtmParamsFromUrl();
  }, []);

  const plans = apiPlans;

  const maxYearlyDiscountPct = useMemo(
    () => computeMaxYearlySavingsPct(apiPlans as any[], isBRL),
    [apiPlans, isBRL],
  );

  const getIconForSlug = (slug?: string | null) => {
    switch (slug) {
      case "ouro":
        return Crown;
      case "prata":
        return Star;
      case "bronze":
      default:
        return Zap;
    }
  };

  const buildFeatures = (plan: any): Array<{ label: string; comingSoon?: boolean; tooltip?: string }> => {
    const items: Array<{ label: string; comingSoon?: boolean; tooltip?: string }> = [];
    const totalSecs = Number(plan?.processing_seconds ?? 0);
    if (totalSecs > 0) items.push({ label: t('pricing.processingTime', { time: formatHoursMinutes(totalSecs) }) });
    const maxClips = Number(plan?.max_clips ?? 0);
    if (maxClips > 0) items.push({ label: maxClips >= 1000000 ? t('pricing.unlimitedClips') : t('pricing.clipsPerMonth', { clips: maxClips }) });
    const maxExpiryDays = Number(plan?.max_expiry_days ?? 0);
    if (maxExpiryDays > 0) items.push({ label: t('pricing.projectsKeptDays', { days: maxExpiryDays }), tooltip: t('pricing.projectsKeptDaysTooltip') });
    const planValue = Number(plan?.value ?? 0);
    if (planValue > 0) items.push({ label: t('pricing.priorityRendering'), tooltip: t('pricing.priorityRenderingTooltip') });
    const desc = Array.isArray(plan?.description_items) ? plan.description_items : [];
    for (const d of desc) {
      if (d && typeof d === "object" && "label" in d) {
        const label = isBRL ? String((d as any).label) : String((d as any).label_en || (d as any).label);
        items.push({ label, comingSoon: Boolean((d as any).coming_soon) });
      } else if (typeof d === "string") {
        items.push({ label: d });
      }
    }
    return items;
  };

  const handleCheckout = (plan: any) => {
    if (!plan.documentId) {
      return;
    }

    // Store the selected plan and show the modal
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);

    // Navigate to the checkout page with the selected plan
    if (selectedPlan?.documentId) {
      // Pegar o parâmetro coupon da URL atual se existir
      const searchParams = new URLSearchParams(window.location.search);
      const couponParam = searchParams.get('coupon');

      // Construir a URL com os parâmetros
      const params = new URLSearchParams();
      params.set('yearly', String(isYearly));
      if (couponParam) {
        params.set('coupon', couponParam);
      }

      navigate(`/plans/${selectedPlan.documentId}?${params.toString()}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="flex-1 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header with logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-16 w-auto"
            />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-4">
              {t('pricing.title')}
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
            {error && (
              <p className="text-sm text-red-400 mt-2">{t('pricing.failedToLoadPlans')}</p>
            )}
          </div>

          <div className="flex items-center justify-center gap-3 mb-10">
            <span className={`text-sm ${!isYearly ? 'font-semibold text-white' : 'text-gray-400'}`}>{t('pricing.monthly')}</span>
            <Switch checked={isYearly} onCheckedChange={setIsYearly} />
            <span className={`text-sm ${isYearly ? 'font-semibold text-white' : 'text-gray-400'}`}>{t('pricing.yearly')}</span>
            {maxYearlyDiscountPct > 0 && (
              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded border border-primary/20">
                {t('pricing.saveUpTo', { percent: maxYearlyDiscountPct })}
              </span>
            )}
          </div>

          {loading ? (
            <div className="text-center text-sm text-gray-400">{t('pricing.loadingPlans')}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {plans.map((plan: any, index: number) => {
                const IconComponent = getIconForSlug(plan?.slug ?? undefined);
                const isPopular = (plan?.order ?? 0) === 2 || (plan?.slug ?? '') === 'prata';
                const { value: priceValue, valueYearly: yearlyTotal } = getPlanPrices(plan, isBRL);
                const yearlyMonthlyEquivalent = yearlyTotal > 0 ? yearlyTotal / 12 : 0;

                const features = buildFeatures(plan);
                return (
                  <Card
                    key={plan?.slug ?? index}
                    className={`relative overflow-hidden transition-all duration-300 bg-gray-800 border-gray-700 ${
                      isPopular
                        ? 'border-primary/40 shadow-glow bg-gradient-to-br from-primary/5 to-primary/10 hover:scale-105'
                        : 'hover:border-primary/20 hover:scale-105'
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute top-0 left-0 right-0">
                        <div className="bg-gradient-primary text-black font-bold text-center py-2 text-sm font-medium">
                          {t('pricing.mostPopular')}
                        </div>
                      </div>
                    )}

                    <CardHeader className={`text-center ${isPopular ? 'pt-12' : 'pt-6'}`}>
                      <div className={`w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-4`} style={{ border: `2px solid ${plan?.color ?? 'transparent'}` }}>
                        <IconComponent className={`w-8 h-8`} style={{ color: plan?.color ?? undefined }} />
                      </div>

                      <CardTitle className="text-2xl font-bold text-white">{plan?.name}</CardTitle>
                      {isYearly ? (
                        <>
                          {yearlyTotal > 0 ? (
                            <>
                              <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                                {formatCurrency(yearlyMonthlyEquivalent)}
                                <span className="text-base text-gray-400 font-normal"> {t('common.perMonth')}</span>
                              </p>
                              <div className="text-center">
                                <span className="text-sm text-gray-400">{t('common.billedAnnually')}</span>
                                <p className="text-sm text-gray-400 mt-1">
                                  {t('checkout.annualTotal')} {formatCurrency(yearlyTotal)}
                                </p>
                                <WalletPlanDiscountNote
                                  plan={plan}
                                  priceReais={yearlyMonthlyEquivalent}
                                  formatCurrency={formatCurrency}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                                {t('pricing.free', 'Free')}
                              </p>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {priceValue > 0 ? (
                            <>
                              <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                                {formatCurrency(priceValue)}
                              </p>
                              <div className="text-center">
                                <span className="text-base text-gray-400">{t('common.perMonth')}</span>
                                <div>
                                  <WalletPlanDiscountNote
                                    plan={plan}
                                    priceReais={priceValue}
                                    formatCurrency={formatCurrency}
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                                {t('pricing.free', 'Free')}
                              </p>
                            </>
                          )}
                        </>
                      )}
                    </CardHeader>

                    <CardContent className="space-y-6">
                      {priceValue > 0 && (
                        <Button
                          variant={isPopular ? 'default' : 'outline'}
                          className="w-full"
                          size="lg"
                          onClick={() => handleCheckout(plan)}
                        >
                          {isYearly ? t('pricing.selectYearlyPlan') : t('pricing.selectPlan')}
                        </Button>
                      )}

                      <div className="space-y-3">
                        {features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className={`text-sm ${feature.comingSoon ? 'text-gray-400' : 'text-gray-200'}`}>
                              {feature.label}{feature.comingSoon ? ` (${t('pricing.comingSoon', 'Coming soon')})` : ''}
                              {feature.tooltip && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-3.5 h-3.5 text-gray-400 inline-block ml-1 cursor-help align-text-bottom" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      {feature.tooltip}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="text-center mt-12">
            <p className="text-gray-400">
              {t('pricing.haveQuestions')}{" "}
              <a
                href={createWhatsAppUrl(t('plansExtra.whatsappMessage'))}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline font-medium"
              >
                {t('pricing.contactSupport')}
              </a>{" "}
              {t('pricing.customPlanMessage')}
            </p>
          </div>
        </div>
      </div>

      <div className="fixed bottom-4 left-4 z-50">
        <LanguageSwitcher />
      </div>

      <LoginRegisterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default PlansList;

